import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import { cn } from '@/lib/utils'
import type { ProactiveSettings } from '@/types/marketingProactive'
import { BUSINESS_TYPE_LABEL } from './proactiveMeta'

/** What kind of place this is, where the weather comes from, and alerts. */
export function ProactiveSettingsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const query = useQuery({
    queryKey: ['marketing', 'proactive-settings'],
    queryFn: () => apiClient.getProactiveSettings(),
    enabled: open,
  })
  const data = query.data?.data
  const [draft, setDraft] = useState<ProactiveSettings | null>(null)
  useEffect(() => {
    if (open && data) setDraft(data.settings)
  }, [open, data])

  const save = useMutation({
    mutationFn: (s: ProactiveSettings) => apiClient.updateProactiveSettings(s),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing'] })
      toast({ title: 'Settings saved', description: 'New ideas follow these settings from the next check.' })
      onOpenChange(false)
    },
    onError: (err) => toast({ title: 'Could not save', description: describeApiError(err).message, variant: 'destructive' }),
  })

  const toggleType = (t: string) => {
    if (!draft) return
    const has = draft.business_types.includes(t)
    setDraft({ ...draft, business_types: has ? draft.business_types.filter((x) => x !== t) : [...draft.business_types, t] })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Marketing ideas settings</DialogTitle>
          <DialogDescription>Shape which moments and ideas you see.</DialogDescription>
        </DialogHeader>
        {!draft || !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>What kind of place are you?</Label>
              <p className="text-xs text-muted-foreground">Pick all that fit. None picked shows every moment.</p>
              <div className="flex flex-wrap gap-2">
                {data.business_types.map((t) => {
                  const on = draft.business_types.includes(t)
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleType(t)}
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-sm transition-colors',
                        on ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground hover:text-foreground',
                      )}
                      aria-pressed={on}
                    >
                      {BUSINESS_TYPE_LABEL[t] ?? t}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>City</Label>
                <Select value={draft.city || 'none'} onValueChange={(v) => setDraft({ ...draft, city: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {data.cities.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {data.weather_location ? `Weather is read for ${data.weather_location}.` : 'Used for weather ideas.'} A map pin
                  on your website's locations takes priority.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Language for new kits</Label>
                <Select value={draft.language} onValueChange={(v) => setDraft({ ...draft, language: v as ProactiveSettings['language'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="roman_urdu">Roman Urdu mix</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {data.ai_configured ? 'Claude writes kit copy.' : 'Kit copy uses templates (AI writing is not set up on this account).'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Weather ideas</p>
                  <p className="text-xs text-muted-foreground">Rain, heatwaves and cold snaps in the next three days.</p>
                </div>
                <Switch checked={draft.weather_enabled} onCheckedChange={(v) => setDraft({ ...draft, weather_enabled: v })} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Email me new ideas</p>
                  <p className="text-xs text-muted-foreground">
                    {data.owner_email_set ? (
                      'One short email when a moment, expiring stock or the weather calls for action.'
                    ) : (
                      <>
                        Needs an owner email —{' '}
                        <Link to="/admin/marketing/automations" className="text-primary hover:underline">
                          add it under Weekly digest
                        </Link>
                        .
                      </>
                    )}
                  </p>
                </div>
                <Switch checked={draft.alerts_enabled} onCheckedChange={(v) => setDraft({ ...draft, alerts_enabled: v })} />
              </div>
            </div>

            {draft.hidden_moments.length > 0 && (
              <div className="space-y-2">
                <Label>Hidden for good</Label>
                <div className="flex flex-wrap gap-2">
                  {draft.hidden_moments.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setDraft({ ...draft, hidden_moments: draft.hidden_moments.filter((x) => x !== k) })}
                      className="rounded-md border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      {k.replace(/_/g, ' ')} · show again
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!draft || save.isPending} onClick={() => draft && save.mutate(draft)}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
